import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { lastValueFrom } from 'rxjs';
import { activateLanguage } from '../transloco/language-preference';
import { UserService } from './user.service';

export interface AppLanguage {
    /** ISO code used by transloco, the translation api and `user_settings_language`. */
    id: string;
    lngId: string;
    /** Name from `lng_language`, in German or English. */
    name: string;
}

interface LanguageDto {
    lng_id: string;
    lng_name: string;
    lng_iso_1: string | null;
    lng_iso_3: string | null;
}

export const DEFAULT_LANGUAGES: AppLanguage[] = [
    { id: 'de', lngId: '15', name: 'Deutsch' },
    { id: 'en', lngId: '11', name: 'English' },
];

/** The released languages of the last load — the sign-in page has no session to ask. */
export const APP_LANGUAGES_KEY = 'appLanguages';

/** Language → flag in assets/images/flags. */
const FLAGS: Record<string, string> = {
    bg: 'BG',
    bs: 'BA',
    cs: 'CZ',
    da: 'DK',
    de: 'DE',
    el: 'GR',
    en: 'US',
    es: 'ES',
    et: 'EE',
    fa: 'IR',
    fi: 'FI',
    fr: 'FR',
    ga: 'IE',
    gsw: 'CH',
    hi: 'IN',
    hr: 'HR',
    hu: 'HU',
    is: 'IS',
    it: 'IT',
    ja: 'JP',
    ko: 'KR',
    lt: 'LT',
    lv: 'LV',
    mk: 'MK',
    mn: 'MN',
    ms: 'MY',
    nl: 'NL',
    no: 'NO',
    pl: 'PL',
    pt: 'PT',
    ro: 'RO',
    ru: 'RU',
    sk: 'SK',
    sl: 'SI',
    sq: 'AL',
    sr: 'RS',
    sv: 'SE',
    th: 'TH',
    tr: 'TR',
    uk: 'UA',
    zh: 'CN',
};

/**
 * The languages a user can pick: every language released in the system
 * translations, read from Common-Fetch-Lists → Language.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
    readonly languages = signal<AppLanguage[]>(DEFAULT_LANGUAGES);

    private loading: Promise<void> | null = null;
    /** Bumped by every `activate()` — an older switch never overrides a newer one. */
    private activations = 0;
    private pendingActivations = 0;
    private loadedFromBackend = false;
    private displayNames = new Map<string, Intl.DisplayNames | null>();

    constructor(
        private httpClient: HttpClient,
        private userService: UserService,
        private translocoService: TranslocoService
    ) {}

    /** Runs before the first translation load, so the last picked language is accepted. */
    restore(): void {
        const stored = this.readStored();
        if (stored.length) {
            this.apply(stored);
        }
    }

    load(): Promise<void> {
        if (!this.userService.getUser()?.uid) {
            return Promise.resolve();
        }
        if (!this.loading) {
            this.loading = this.fetch().finally(() => (this.loading = null));
        }
        return this.loading;
    }

    /** Switches to `lang` if it is released, otherwise keeps the current language. */
    async activate(lang: string): Promise<void> {
        if (!lang) {
            return;
        }
        const activation = ++this.activations;
        this.pendingActivations++;
        try {
            // A running refresh may just be dropping a locked language.
            if (this.loading || !this.find(lang)) {
                await this.load();
            }
            if (this.find(lang)) {
                await activateLanguage(
                    this.translocoService,
                    lang,
                    () => this.activations === activation && !!this.find(lang)
                );
            }
        } finally {
            this.pendingActivations--;
        }
        this.leaveLockedLanguage();
    }

    find(id: string): AppLanguage | undefined {
        return this.languages().find(language => language.id === id);
    }

    /** Options for a select bound to `current` — a stored language that was locked since stays visible. */
    optionsFor(current: string | null | undefined): AppLanguage[] {
        const languages = this.languages();
        if (!current || languages.some(language => language.id === current)) {
            return languages;
        }
        return [...languages, { id: current, lngId: '', name: current }];
    }

    /** Name of the language in `displayLang` — by default in the language itself. */
    label(id: string, displayLang: string = id): string {
        let name: string | undefined;
        try {
            name = this.displayNamesFor(displayLang)?.of(id);
        } catch {}
        if (name && name.toLowerCase() !== id) {
            return (
                name.charAt(0).toLocaleUpperCase(displayLang) + name.slice(1)
            );
        }
        return this.find(id)?.name || id.toUpperCase();
    }

    flag(id: string): string | null {
        return FLAGS[id] ?? null;
    }

    private displayNamesFor(lang: string): Intl.DisplayNames | null {
        if (!this.displayNames.has(lang)) {
            let displayNames: Intl.DisplayNames | null = null;
            try {
                displayNames = new Intl.DisplayNames([lang, 'en'], {
                    type: 'language',
                });
            } catch {}
            this.displayNames.set(lang, displayNames);
        }
        return this.displayNames.get(lang) ?? null;
    }

    private async fetch(): Promise<void> {
        const formData = new FormData();
        formData.append('option', 'language');
        // The names come in German or English — like every other common list.
        formData.append(
            'tln_id',
            this.translocoService.getActiveLang() === 'de' ? '15' : '11'
        );
        // 0 = released languages only
        formData.append('lng_id', '0');
        try {
            const response = await lastValueFrom(
                this.httpClient.post<{ result?: LanguageDto[] }>(
                    `hippo/ajax_navigator.php?page=pages/api/Ajax_common_lists.php&uid=${this.userService.getUser()?.uid}&page_type=json`,
                    formData
                )
            );
            const languages = this.toLanguages(response?.result);
            if (languages.length) {
                this.loadedFromBackend = true;
                this.apply(languages);
                this.store(this.languages());
                this.leaveLockedLanguage();
            }
        } catch {
            // Keep the known languages — the selection must never end up empty.
        }
    }

    private toLanguages(list: LanguageDto[] | undefined): AppLanguage[] {
        const languages: AppLanguage[] = [];
        for (const dto of Array.isArray(list) ? list : []) {
            const id = (dto?.lng_iso_1 || dto?.lng_iso_3 || '')
                .trim()
                .toLowerCase();
            if (id && !languages.some(language => language.id === id)) {
                languages.push({
                    id,
                    lngId: String(dto.lng_id ?? ''),
                    name: dto.lng_name || id.toUpperCase(),
                });
            }
        }
        return languages;
    }

    private apply(languages: AppLanguage[]): void {
        // German and English carry the whole app and the fallback texts — they always stay.
        const complete = [
            ...DEFAULT_LANGUAGES.filter(
                base => !languages.some(language => language.id === base.id)
            ),
            ...languages,
        ];
        // Sorted by their own names, so the order does not follow the language the list was fetched in.
        complete.sort((a, b) =>
            this.label(a.id).localeCompare(this.label(b.id))
        );
        this.languages.set(complete);
        this.translocoService.setAvailableLangs(
            complete.map(language => ({
                id: language.id,
                label: language.name,
            }))
        );
    }

    /** A language that was locked in the meantime cannot be served anymore. */
    private leaveLockedLanguage(): void {
        const active = this.translocoService.getActiveLang();
        if (
            !this.loadedFromBackend ||
            this.pendingActivations ||
            !active ||
            this.find(active)
        ) {
            return;
        }
        const activation = this.activations;
        void activateLanguage(
            this.translocoService,
            this.translocoService.getDefaultLang(),
            () => this.activations === activation
        );
    }

    // localStorage can be unavailable (private mode, blocked storage) — never throw.
    private readStored(): AppLanguage[] {
        try {
            const parsed = JSON.parse(
                localStorage.getItem(APP_LANGUAGES_KEY) ?? '[]'
            );
            return Array.isArray(parsed)
                ? parsed.filter(
                      (language: AppLanguage) =>
                          typeof language?.id === 'string' && !!language.id
                  )
                : [];
        } catch {
            return [];
        }
    }

    private store(languages: AppLanguage[]): void {
        try {
            localStorage.setItem(APP_LANGUAGES_KEY, JSON.stringify(languages));
        } catch {}
    }
}
